import { useDocumentNumber } from '@api/hooks/useTransactions';
import { ControlledTextInput } from '@components/ControlledTextInput';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface DocumentNumberInputProps {
	accountId?: string | number;
	disabled?: boolean;
}

export const DocumentNumberInput: FC<DocumentNumberInputProps> = ({
	accountId,
	disabled,
}) => {
	const { t } = useTranslation();
	const {
		setValue,
		formState: { submitCount },
	} = useFormContext();
	const { data, isSuccess, isLoading, refetch } = useDocumentNumber(accountId);

	useEffect(() => {
		if (isSuccess) {
			// shouldValidate recomputes formState.isValid — without it, a form
			// whose other fields finished validating before the document number
			// arrived keeps isValid=false forever (submit stays disabled), since
			// no later event re-runs validation.
			setValue('document_number', data?.documentNumber, { shouldValidate: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, isSuccess]);

	useEffect(() => {
		if (!submitCount && accountId) {
			void refetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [submitCount, accountId]);

	return (
		<ControlledTextInput
			disabled={disabled}
			isLoading={isLoading}
			label={t('payments.newPayment.data.docNo')}
			name="document_number"
			rules={{
				required: t('payments.newPayment.fieldIsRequired'),
				maxLength: {
					message: t('payments.common.maxLength', { maxLength: 35 }),
					value: 35,
				},
			}}
		/>
	);
};
